// Diamond packs for real money (shop tab #/shop/packs) and the admin page's order list.
// The server says whether the store is on (GET /api/store); while it is off nothing here shows.
// Buying opens Stripe Checkout; the diamonds arrive by webhook and a sync, so the page needs no reload.
import { psecTabs, psecWrap } from './psec.ts';
import { DIAMOND_PACKS, STARTER, packById, starterLines, starterOffered, usd, type DiamondPack, type StoreAdmin, type StoreInfo } from '../shared/diamond-packs.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { confirmClick, isArmed, CONFIRM_MONEY_MS } from './confirm.ts';
import { ITEM } from '../shared/items.ts';
import { OUTFIT } from '../shared/outfits.ts';
import { bestHunt, potionFor } from '../shared/quests.ts';
import { iconUrl, spriteUrl, has } from './assets.ts';
import { esc, full } from './fmt.ts';
import { toast } from './dialog.ts';
import { S } from './state.ts';
import { go } from './router.ts';
import { mySprite } from './trainerart.ts';
import { GEM_PACKS, gemPackById } from '../shared/gems.ts';
import { GEM_ICON } from './money.ts';

let info: StoreInfo | null = null;
let busy = '';
let infoAt = 0;
let redraw: () => void = () => {};
let listening = false;

/**
 * Asks the server whether the store is on for this account (GET /api/store) and redraws when that changed.
 * Run at start (after login), when the Shop draws (at most once a minute) and before a buy, so a page left
 * open across the real-money switch, or a tester's new account, never keeps a stale "off".
 */
async function loadStore(): Promise<StoreInfo | null> {
  infoAt = Date.now();
  try {
    const r = await fetch('/api/store', { headers: authHeader() });
    const x = r.ok ? (await r.json()) as StoreInfo : null;
    const was = !!info?.enabled;
    if (x) info = x;
    if (!!info?.enabled !== was) redraw();
  } catch { /* offline for a moment: the next check tries again */ }
  return info;
}

export const storeOn = () => !!info?.enabled;

// Diamond packs bought with gems (one way, at the card price, 1 gem = 1 cent), while gems are open.
let send: (m: ClientMsg) => void = () => {};
let gem: { on: boolean; bal: number } | null = null;
let gemAsked = 0;
export function bindStoreGems(o: { send: typeof send }) { send = o.send; }
/** The server's gem balance reply. */
export function onGemBal(m: { on: boolean; bal: number }) { gem = { on: !!m.on, bal: Number(m.bal) || 0 }; }
/** Asks for the gem balance now and then (the Shop calls this as it draws). */
export function askGems() {
  const now = Date.now();
  if (now - infoAt > 60_000) void loadStore();
  if (now - gemAsked < 30_000) return;
  gemAsked = now;
  queueMicrotask(() => send({ t: 'gemBal' }));
}
const gemsOn = () => !!gem?.on;
/** The gems the header shows: the balance while gems are open, else null (nothing shows). */
export const headerGems = (): number | null => (gem?.on ? gem.bal : null);
/** Whether the Diamond packs tab shows: card packs, or gem prices for them. */
export const packsOn = () => storeOn() || gemsOn();
/** How a phone prices the diamond packs: by card or in gems (one price button per row; wider screens show both). */
let payBy: 'card' | 'gems' = 'card';
export function payByClick(id: string) { payBy = id === 'gems' ? 'gems' : 'card'; redraw(); }

/** A click on a pack's gem price: the second click buys. */
export function gemPackClick(id: string) {
  const p = packById(id);
  if (!p || !gem?.on) return;
  if (gem.bal < p.cents) { toast('Not enough gems.', 'err'); return; }
  if (confirmClick(`gemdp:${id}`, CONFIRM_MONEY_MS)) send({ t: 'gemDiamonds', pack: id, price: p.cents });
}

/** Loads the store flag once; rerender runs when it turns out to be on. Also handles the return from Checkout. */
export function initStore(rerender: () => void) {
  redraw = rerender;
  const q = new URLSearchParams(location.search);
  const back = q.get('purchase');
  if (back) {
    const order = q.get('order') ?? '';
    q.delete('purchase'); q.delete('order');
    const rest = q.toString();
    history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
    // Cancel: nothing to announce, the shop is simply back.
    if (back === 'success' && /^ord_[0-9a-f]{24}$/.test(order)) openPurchase(order);
  }
  void loadStore();
  if (listening) return;
  listening = true;
  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest('[data-act="pack-buy"]') as HTMLElement | null;
    if (el) void buy(el.dataset.id ?? '', () => redraw());
  });
}

function authHeader(): Record<string, string> {
  let token = '';
  try { token = localStorage.getItem('mi-token') ?? ''; } catch { /* storage off */ }
  return { authorization: `Bearer ${token}` };
}

// ---------------- Purchase complete ----------------
// Back from Checkout with ?purchase=success&order=…: a framed panel over the game (the hunt keeps running).
// The webhook may land after the redirect, so the panel asks for its order until it is paid, then counts
// the diamonds up. After a minute it settles into a calm line; the socket's credit still finishes it.
type Phase = 'confirming' | 'paid' | 'late' | 'failed' | 'refunded' | 'missing';
const PACK_NAMES: Record<string, string> = { [STARTER.id]: 'Starter Pack', d60: 'Handful of Diamonds', d130: 'Pouch of Diamonds', d350: 'Satchel of Diamonds', d1000: 'Coffer of Diamonds', d2000: 'Hoard of Diamonds' };
const POLL_MS = 3000, LATE_MS = 60_000, COUNT_MS = 1400;
interface Purchase { order: string; pack: string; diamonds: number; phase: Phase; opened: number; poll: number | null; counted: boolean; closed: boolean }
let pur: Purchase | null = null;
let layer: HTMLElement | null = null;
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

function openPurchase(order: string) {
  pur = { order, pack: '', diamonds: 0, phase: 'confirming', opened: performance.now(), poll: null, counted: false, closed: false };
  drawPurchase();
  void pollPurchase();
}

async function pollPurchase() {
  const p = pur;
  if (!p || p.phase !== 'confirming') return;
  try {
    const r = await fetch(`/api/store/order?id=${encodeURIComponent(p.order)}`, { headers: authHeader() });
    if (pur !== p) return;
    if (r.status === 404 || r.status === 401) { setPhase('missing'); return; }
    if (r.ok) {
      const b = (await r.json()) as { status: string; pack: string; diamonds: number };
      if (pur !== p) return;
      const changed = p.pack !== b.pack;
      p.pack = b.pack; p.diamonds = b.diamonds;
      if (b.status === 'paid') { setPhase('paid'); return; }
      if (b.status === 'failed' || b.status === 'expired') { setPhase('failed'); return; }
      if (b.status === 'refunded') { setPhase('refunded'); return; }
      if (changed) drawPurchase();
    }
  } catch { /* offline for a moment: try again */ }
  if (pur !== p || p.phase !== 'confirming') return;
  if (performance.now() - p.opened >= LATE_MS) { setPhase('late'); return; }
  p.poll = window.setTimeout(() => { void pollPurchase(); }, POLL_MS);
}

function setPhase(phase: Phase) {
  if (!pur) return;
  if (pur.poll !== null) { clearTimeout(pur.poll); pur.poll = null; }
  pur.phase = phase;
  if (!pur.closed) drawPurchase();
}

/** The socket's credit notice. True when it belongs to the order on the return panel (which then shows it instead of a toast). */
export function purchaseCredited(n: { order?: unknown; diamonds?: unknown } | undefined): boolean {
  if (!pur || !n || n.order !== pur.order) return false;
  if (typeof n.diamonds === 'number') pur.diamonds = n.diamonds;
  pur.closed = false;
  if (pur.phase !== 'paid') setPhase('paid'); else drawPurchase();
  return true;
}

function closePurchase() {
  if (!pur) return;
  pur.closed = true;
  // Still waiting: keep listening on the socket so the credit can reopen the panel; nothing more to fetch.
  if (pur.poll !== null) { clearTimeout(pur.poll); pur.poll = null; }
  if (pur.phase === 'confirming') pur.phase = 'late';
  else if (pur.phase !== 'late') pur = null;
  layer?.remove(); layer = null;
}

function countUp(el: HTMLElement, from: number, to: number, prefix = '') {
  if (reduced() || to <= from) { el.textContent = `${prefix}${full(to)}`; return; }
  const t0 = performance.now();
  const step = (t: number) => {
    if (!el.isConnected) return;
    const k = Math.min(1, (t - t0) / COUNT_MS), e = 1 - (1 - k) ** 3;
    el.textContent = `${prefix}${full(Math.round(from + (to - from) * e))}`;
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function drawPurchase() {
  const p = pur;
  if (!p || p.closed) return;
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'pdone-layer';
    layer.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === layer || t.closest('[data-pdone-close]')) closePurchase();
      else if (t.closest('[data-pdone-go]')) { const to = (t.closest('[data-pdone-go]') as HTMLElement).dataset.pdoneGo!; closePurchase(); go(to); }
    });
    document.body.appendChild(layer);
  }
  const gem = gemPackById(p.pack);
  const name = gem ? `${full(gem.gems)} Gems` : PACK_NAMES[p.pack] ?? (p.diamonds ? `${full(p.diamonds)} Diamonds` : 'Diamonds');
  const n = gem ? gem.gems : p.diamonds || packById(p.pack)?.diamonds || 0;
  const icon = gem ? GEM_ICON : iconUrl('diamond');
  const head = { confirming: 'Confirming payment', paid: 'Purchase complete', late: 'Payment received', failed: 'Payment not completed', refunded: 'Purchase refunded', missing: 'Order not found' }[p.phase];
  const line = { confirming: '', paid: '', late: p.pack === STARTER.id ? 'Your Starter Pack will arrive shortly.' : gem ? 'Your gems will arrive shortly.' : 'Your diamonds will arrive shortly.', failed: 'No charge was made.', refunded: 'This purchase was refunded.', missing: 'This order is not on your account.' }[p.phase];
  const ok = p.phase === 'paid', dim = p.phase === 'failed' || p.phase === 'refunded' || p.phase === 'missing';
  const bal = (gem ? S.market?.gems?.bal : S.trainer?.diamonds) ?? 0;
  const starter = p.pack === STARTER.id;
  const count = starter ? (ok ? `<ul class="pdone-list sp-list">${starterContents()}</ul>` : '')
    : ok ? `<b class="pdone-n" data-n>+${full(p.counted ? n : 0)}</b>`
    : dim ? '' : `<b class="pdone-n">${p.phase === 'confirming' ? '<span class="pdone-spin" aria-hidden="true">✦</span>' : ''}${n ? `+${full(n)}` : ''}</b>`;
  layer.innerHTML = `<section class="pdone panel pdone-${p.phase}" role="dialog" aria-labelledby="pdone-h" aria-live="polite">
    <button class="btn tiny pdone-x" data-pdone-close aria-label="Close">✕</button>
    <p class="pdone-eye" id="pdone-h">${head}</p>
    <div class="pdone-seal" aria-hidden="true">${ok ? '<i></i><i></i><i></i><i></i><i></i>' : ''}${starter ? `<img class="sp-fit" src="${spriteUrl(`${mySprite(STARTER.outfit)}-down-0`)}" alt="">` : `<img src="${icon}" alt="">`}</div>
    <h3 class="pdone-name">${esc(name)}</h3>
    ${count}
    ${ok && !starter ? `<p class="pdone-bal"><img src="${icon}" alt=""><span data-bal>${full(p.counted ? bal : Math.max(0, bal - n))}</span></p>` : ''}
    ${line ? `<p class="pdone-line">${line}</p>` : ''}
    <div class="pdone-acts">${ok ? `<button class="btn" data-pdone-go="${gem ? '#/market/gems' : '#/shop/diamonds'}">${gem ? 'Go to Market' : 'Go to Shop'}</button>` : ''}<button class="btn gold" data-pdone-go="#/hunt">Back to the game</button></div>
  </section>`;
  const back = layer.querySelector<HTMLElement>('.pdone-acts .gold');
  back?.focus({ preventScroll: true });
  if (ok && !p.counted && !starter) {
    p.counted = true;
    countUp(layer.querySelector<HTMLElement>('[data-n]')!, 0, n, '+');
    // The balance: from before the credit up to now (the sync carrying it may land a moment later).
    window.setTimeout(() => {
      const el = layer?.querySelector<HTMLElement>('[data-bal]');
      if (!el) return;
      const now = (gem ? S.market?.gems?.bal : S.trainer?.diamonds) ?? bal;
      countUp(el, Math.max(0, now - n), now);
    }, reduced() ? 0 : 500);
  }
}

document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && layer) { e.preventDefault(); closePurchase(); } });

async function buy(pack: string, rerender: () => void) {
  if (busy) return;
  if (!storeOn()) {
    const st = await loadStore();
    if (!st?.enabled) { toast(st ? "The shop isn't open yet." : "The shop couldn't be reached. Refresh the page.", 'err'); return; }
  }
  busy = pack; rerender();
  try {
    const r = await fetch('/api/store/checkout', { method: 'POST', headers: { 'content-type': 'application/json', ...authHeader() }, body: JSON.stringify({ pack }) });
    const b = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
    if (r.ok && b.url) { location.href = b.url; return; }
    toast(b.error ?? 'The payment page could not be opened.', 'err');
  } catch {
    toast('The payment page could not be opened.', 'err');
  }
  busy = ''; rerender();
}

/** Diamonds per dollar against the smallest pack, as a whole percent more (0 for the smallest). */
function packMore(p: DiamondPack, packs: DiamondPack[]): number {
  const base = packs.reduce((a, x) => (x.cents < a.cents ? x : a), packs[0]);
  return Math.max(0, Math.round(((p.diamonds / p.cents) / (base.diamonds / base.cents) - 1) * 100));
}

/** A pack's painted still. It is drawn at each size the Shop shows it (art/rig3d/parts/packs.ts PACK_SIZES), and
 *  watchArtIcons (client/assets.ts) gives each screen the drawing made for it: 128 px, 160 on a laptop at 125%, 256 on
 *  a 2x screen, 96 for a Gems row on a 2x phone. */
const packStill = (id: string, cls: string) => `<img class="${cls}" src="art/pack-${id}.png" alt="">`;

/** A pack's picture: its own still once drawn (pack-<id>), else a pile of diamonds that grows with the pack. */
function packArt(p: DiamondPack, rank: number) {
  if (has(`pack-${p.id}`)) return packStill(p.id, 'dp-still');
  const n = rank + 1;
  return `<span class="dp-pile n${n}" aria-hidden="true">${Array.from({ length: n * 2 - 1 }, () => `<img src="${iconUrl('diamond', 64)}" alt="">`).join('')}</span>`;
}

function packCard(p: DiamondPack, i: number, packs: DiamondPack[]) {
  const more = packMore(p, packs);
  const tag = i === packs.length - 1 ? 'Best value' : i === 2 ? 'Popular' : '';
  return `<div class="dp-card${tag ? ' tagged' : ''}${i === packs.length - 1 ? ' best' : ''}">${tag ? `<span class="pz-tag">${tag}</span>` : ''}
    <div class="dp-art">${packArt(p, i)}</div>
    <span class="dp-name">${esc(PACK_NAMES[p.id] ?? 'Diamonds')}</span>
    <b class="dp-n"><img src="${iconUrl('diamond')}" alt="">${full(p.diamonds)}</b>
    <span class="dp-more">${more ? `+${more}% more` : '&nbsp;'}</span>
    ${storeOn() ? `<button class="btn gold" data-act="pack-buy" data-id="${esc(p.id)}"${busy ? ' disabled' : ''}>${busy === p.id ? 'Opening checkout…' : usd(p.cents)}</button>` : ''}
    ${gemsOn() ? `<button class="btn dp-gem${isArmed(`gemdp:${p.id}`) ? ' gold armed' : ''}" data-act="pack-gems" data-id="${esc(p.id)}"${(gem?.bal ?? 0) < p.cents ? ' disabled' : ''}><img src="${GEM_ICON}" alt="">${isArmed(`gemdp:${p.id}`) ? `Confirm ${full(p.cents)}` : `${full(p.cents)} gems`}</button>` : ''}</div>`;
}

/** The Starter Pack's contents as list items with icons. */
function starterContents() {
  const lvl = S.trainer?.level ?? 1, potion = potionFor(bestHunt(lvl).level);
  const [boost, coins, seals, potions, fit] = starterLines(`${ITEM[potion]?.name ?? 'Healing potion'}s`, `${ITEM[STARTER.seal]?.name ?? 'Seal'}s`, OUTFIT[STARTER.outfit]?.name ?? 'Starter outfit');
  const li = (icon: string, text: string) => `<li><img src="${icon}" alt="">${esc(text)}</li>`;
  return li(iconUrl('boost-xp'), boost) + li(iconUrl('coin'), coins) + li(iconUrl(STARTER.seal), seals) + li(iconUrl(potion), potions) + li(spriteUrl(`${mySprite(STARTER.outfit)}-down-0`), fit);
}

/** The Starter Pack card, first on the Diamond packs tab until it is bought. */
function starterCard() {
  if (!starterOffered(info, S.trainer) || !info?.starter) return '';
  const id = info.starter.id;
  return `<section class="panel sf-pass sp-feat" aria-label="Starter Pack"><div class="sf-pass-art sp-art">${has('pack-starter') ? packStill('starter', 'pz-show-pic sp-still') : `<img class="pz-show-pic" src="${spriteUrl(`${mySprite(STARTER.outfit)}-down-0`)}" alt="">`}</div>
    <div class="sf-pass-body"><span class="pz-eyebrow">Once per account</span><h3>Starter Pack</h3>
    <ul class="sp-list">${starterContents()}</ul>
    <button class="btn gold" data-act="pack-buy" data-id="${esc(id)}"${busy ? ' disabled' : ''}>${busy === id ? 'Opening checkout…' : usd(info.starter.cents)}</button></div></section>`;
}

/** The Diamond packs tab. */
export function packsPane() {
  askGems();
  if (!info?.enabled && !gemsOn()) return '';
  const packs = info?.enabled ? info.packs : DIAMOND_PACKS;
  // On a phone the Starter Pack and the diamond packs take turns under two tabs (client/psec.ts).
  const starter = info?.enabled ? starterCard() : '';
  const keys = ['starter', 'packs'];
  const w = (key: string, html: string) => (starter ? psecWrap('dpacks', key, keys, html) : html);
  return `${info?.mode === 'test' ? '<p class="dpack-test">Test mode: no real charges. Real cards are declined; pay with 4242 4242 4242 4242, any future date and any CVC.</p>' : ''}
    <div class="dp-bar">${starter ? psecTabs('dpacks', [{ key: 'starter', title: 'Starter Pack', dot: true }, { key: 'packs', title: 'Diamonds' }]) : ''}${storeOn() && gemsOn() ? `<div class="dp-pay" role="group" aria-label="Pay with">${(['card', 'gems'] as const).map((k) => `<button class="${payBy === k ? 'on' : ''}" data-act="dp-pay" data-id="${k}" aria-pressed="${payBy === k}">${k === 'card' ? 'Card' : `<img src="${GEM_ICON}" alt="">Gems`}</button>`).join('')}</div>` : ''}</div>
    ${w('starter', starter)}
    ${w('packs', `${gemsOn() ? `<p class="dp-gembal"><img src="${GEM_ICON}" alt="">${full(gem?.bal ?? 0)} gems</p>` : ''}
    <div class="dp-grid${storeOn() && gemsOn() ? ` pay-${payBy}` : ''}">${packs.map((p, i, all) => packCard(p, i, all)).join('')}</div>`)}
    <p class="muted dpack-policy">${info?.enabled ? `Paid by card through Stripe. ` : ''}Diamonds stay on your account and cannot be traded${gemsOn() ? ' or turned back into gems' : ''}.${info?.enabled ? ` Unspent bought diamonds can be refunded within ${info.refundDays} days.` : ''} <a href="/terms" target="_blank" rel="noopener">Terms</a> · <a href="/privacy" target="_blank" rel="noopener">Privacy</a></p>`;
}

const GEM_PACK_NAMES: Record<string, string> = { g500: 'Handful of Gems', g1000: 'Pouch of Gems', g2500: 'Satchel of Gems', g5000: 'Coffer of Gems' };

/** Whether the Shop's Gems tab shows: gem packs sell by card while gems are open (the server's own check, server/store.ts). */
export const gemShopOn = () => storeOn() && gemsOn();

/** A gem pack's picture: its painted still once drawn (art/pack-<id>), else a pile of gems that grows with the pack. */
function gemPackArt(id: string, rank: number) {
  if (has(`pack-${id}`)) return packStill(id, 'dp-still');
  const n = rank + 1;
  return `<span class="dp-pile n${n}" aria-hidden="true">${Array.from({ length: n * 2 - 1 }, () => '<img src="art/icon-gem@64.png" alt="">').join('')}</span>`;
}

/**
 * The Shop's Gems tab (Cole 2026-10-28: he looked for gems in the Shop): the gem packs Market > Gems sells, bought by
 * card the same way. The wallet, history and cash-out stay in the Market.
 */
export function gemPacksPane() {
  askGems();
  if (!gemShopOn()) return '';
  const card = (p: (typeof GEM_PACKS)[number], i: number) => `<div class="dp-card gp-card${i === GEM_PACKS.length - 1 ? ' best' : ''}">
    <div class="dp-art gp-art">${gemPackArt(p.id, i)}</div>
    <span class="dp-name">${GEM_PACK_NAMES[p.id] ?? 'Gems'}</span>
    <b class="dp-n"><img src="${GEM_ICON}" alt="">${full(p.gems)}</b>
    <button class="btn gold" data-act="pack-buy" data-id="${esc(p.id)}"${busy ? ' disabled' : ''}>${busy === p.id ? 'Opening checkout…' : usd(p.cents)}</button></div>`;
  return `${info?.mode === 'test' ? '<p class="dpack-test">Test mode: no real charges. Real cards are declined; pay with 4242 4242 4242 4242, any future date and any CVC.</p>' : ''}
    <p class="dp-gembal"><img src="${GEM_ICON}" alt="">${full(gem?.bal ?? 0)} gems</p>
    <div class="dp-grid gp-grid">${GEM_PACKS.map(card).join('')}</div>
    <p class="muted dpack-policy">Paid by card through Stripe. Bought gems buy on the Market and Diamond packs, and never cash out. <a href="#/market/gems">Gem wallet</a> · <a href="/terms" target="_blank" rel="noopener">Terms</a> · <a href="/privacy" target="_blank" rel="noopener">Privacy</a></p>`;
}

/** Admin page: store mode and recent orders, read-only. */
export function storeAdminSection(s: StoreAdmin | undefined) {
  if (!s) return '';
  const when = (ts: number | null) => (ts ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  const rows = s.orders.map((o) => `<tr><td>${when(o.created)}</td><td>${esc(o.name)}</td><td>${full(o.diamonds)}</td><td>${usd(o.cents)}</td><td class="dorder-${esc(o.status)}">${esc(o.status)}${o.clawed ? ` (−${full(o.clawed)})` : ''}</td><td>${when(o.paid)}</td></tr>`).join('');
  // The real-money switch: live keys are staged on the server once, then this flips the shop for everyone.
  const live = s.liveOn
    ? `<div class="actions"><span class="muted">Real money is on: every player can buy, charged to real cards.</span><button class="btn small" data-act="adm-store-live" data-id="0">Back to test mode</button></div>`
    : s.liveStaged
      ? `<div class="actions"><span class="muted">Live Stripe keys are set. Test mode: only admins and playtest accounts see the shop.</span><button class="btn small gold" data-act="adm-store-live" data-id="1">Turn on real money</button></div>`
      : `<div class="actions"><span class="muted">Test mode: only admins and playtest accounts see the shop. Real money needs live Stripe keys on the server first.</span></div>`;
  return `<div class="row between" style="margin-top:16px"><h3>Diamond store</h3><span class="dstore-mode dstore-${s.mode}">Store: ${s.mode}</span></div>
    ${live}
    <table class="tbl"><tr><th>Created</th><th>Trainer</th><th>Diamonds</th><th>Price</th><th>Status</th><th>Paid</th></tr>${rows || '<tr><td colspan=6>No orders yet</td></tr>'}</table>`;
}
